/**
 * LUNA Coding Tutor - Dashboard Page Component (Premium Redesign)
 * 
 * "Command Center" Aesthetic
 * - Glassmorphism: bg-[#1e293b]/60 backdrop-blur-xl
 * - Neon Accents: Cyan (#22d3ee) & Purple (#a855f7)
 * - Bento Grid Layout
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Line, Radar, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useDashboard, escapeHtml, formatMessageDate, useAnimatedValue } from '@/hooks/useDashboard';
import useLunaStore from '@/store/useLunaStore';
import Header from '../components/layout/Header';
import TermsModal from '../components/modals/TermsModal';
import { getUserFullTitle } from '@/utils/userTitles';

// Chart.js Registration
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  RadialLinearScale, BarElement, Title, Tooltip, Legend, Filler
);

ChartJS.defaults.color = '#94a3b8';
ChartJS.defaults.borderColor = 'rgba(255, 255, 255, 0.05)';
ChartJS.defaults.font.family = "'Space Mono', monospace";

// ==========================================
// Helper Components (Styled)
// ==========================================

function StatCard({ icon, color, title, value, subtext, delay = '0' }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl glass-panel p-5 group hover:border-${color}-500/30 transition-all duration-500 animate-fade-in-up ${delay}`}>
      <div className={`absolute top-0 right-0 w-32 h-32 bg-${color}-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-${color}-500/10 transition-colors duration-700`}></div>

      <div className="relative z-10 flex flex-col h-full justify-between">
        <div className="flex items-start justify-between mb-4">
          <div className="flex flex-col">
            <span className="text-xs font-bold tracking-wider text-slate-500 uppercase font-mono">{title}</span>
          </div>
          <div className={`w-10 h-10 rounded-xl bg-${color}-500/10 flex items-center justify-center text-${color}-400 shadow-[0_0_15px_rgba(0,0,0,0.3)] group-hover:scale-110 transition-transform duration-300`}>
            <i className={icon}></i>
          </div>
        </div>

        <div>
          <div className="text-3xl font-bold text-white tracking-tight font-mono">{value}</div>
          {subtext && <div className="mt-2 text-xs text-slate-400">{subtext}</div>}
        </div>
      </div>
    </div>
  );
}

function InsightItem({ type, title, description, score }) {
  const styles = {
    warning: { border: 'border-red-500/20', icon: 'text-red-400', bg: 'bg-red-500/5' },
    achievement: { border: 'border-amber-500/20', icon: 'text-amber-400', bg: 'bg-amber-500/5' },
    strength: { border: 'border-green-500/20', icon: 'text-green-400', bg: 'bg-green-500/5' },
    weakness: { border: 'border-orange-500/20', icon: 'text-orange-400', bg: 'bg-orange-500/5' },
    recommendation: { border: 'border-cyan-500/20', icon: 'text-cyan-400', bg: 'bg-cyan-500/5' },
  };
  const style = styles[type] || styles.recommendation;

  return (
    <div className={`group flex gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] hover:${style.border} transition-all duration-300`}>
      <div className={`shrink-0 w-10 h-10 rounded-full ${style.bg} ${style.icon} flex items-center justify-center`}>
        <i className={`fas ${type === 'strength' ? 'fa-bolt' : type === 'weakness' ? 'fa-arrow-trend-up' : 'fa-lightbulb'}`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors truncate">{title}</h4>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">{description}</p>
        {score !== undefined && (
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1 flex-1 bg-slate-700/30 rounded-full overflow-hidden">
              <div className="h-full bg-current rounded-full opacity-50" style={{ width: `${score}%`, color: 'currentColor' }}></div>
            </div>
            <span className="text-[10px] font-mono opacity-70">{score}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// Main Component
// ==========================================

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, nickname } = useLunaStore(); // Get username for greeting

  // Destructure hook
  const {
    loading, error, stats, xpProgress, heatmapData,
    globalStats, languageStats, learnedLanguages, selectedLanguage, currentSkillData,
    timeline, insights, trendSummary, problemStats, masteredSkills, languageProblemsList, roadmapPreview,
    messages, unreadCount, messagesPanelOpen,
    setSelectedLanguage, setMessagesPanelOpen, loadDashboard, loadRoadmapPreview, loadMessages,
    markMessageAsRead, markAllMessagesAsRead,
    getActivityChartConfig, getSkillChartConfig, getLanguageCompareChartConfig,
  } = useDashboard();

  // Animated Values
  const animatedLevel = useAnimatedValue(stats.level, 500);
  const animatedXp = useAnimatedValue(stats.totalXp, 800, true);
  const animatedStreak = useAnimatedValue(stats.streak, 600);
  const animatedAttempted = useAnimatedValue(problemStats.attempted, 800);
  const animatedSolved = useAnimatedValue(problemStats.solved, 800);

  // Initial Load
  useEffect(() => {
    loadDashboard();
    loadRoadmapPreview();
    loadMessages();
  }, [loadDashboard, loadRoadmapPreview, loadMessages]);

  // Auth Redirect
  useEffect(() => {
    if (error?.type === 'auth') {
      navigate('/login?redirect=dashboard');
    }
  }, [error, navigate]);

  // Panel Control
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('panel') === 'messages') {
      setMessagesPanelOpen(true);
    }
  }, [location.search, setMessagesPanelOpen]);

  // Chart Configs
  const activityChartConfig = getActivityChartConfig();
  const skillChartConfig = getSkillChartConfig();
  const languageCompareChartConfig = getLanguageCompareChartConfig();

  // Modal State
  const [showTerms, setShowTerms] = React.useState(false);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          <div className="text-cyan-500 font-mono text-sm animate-pulse">SYSTEM INITIALIZING...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-[#0B0E14] text-slate-200 font-sans selection:bg-cyan-500/30 overflow-x-hidden scrollbar-thin scrollbar-track-[#0B0E14] scrollbar-thumb-white/10 hover:scrollbar-thumb-cyan-500/50">
      {/* Background - Grid + Aurora Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="aurora-blob blob-1"></div>
        <div className="aurora-blob blob-2"></div>
        <div className="aurora-blob blob-3"></div>
        <div className="bg-grid-pattern"></div>
      </div>

      {/* Header */}
      <Header
        unreadCount={unreadCount}
        onNavigate={(path) => navigate(path)}
        onNewTopic={() => navigate('/')}
        // Mock handlers for header props
        onShowTerms={() => setShowTerms(true)}
        onPasswordChange={() => { }}
        onLogout={() => { }}
      />

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />

      <main className="max-w-[1400px] mx-auto px-4 py-5 space-y-4">

        {/* 1. Hero Section */}
        <section className="relative mb-5">
          <div className="absolute -left-20 -top-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-[80px] pointer-events-none"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 tracking-wider">
                  LIVE SESSION
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase()}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                Welcome back, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-indigo-400 animate-gradient-x">
                  {nickname || username || 'Commander'}
                </span>
              </h1>
            </div>

            {/* Quick Stats Strip */}
            <div className="flex gap-1 md:gap-4 bg-white/5 backdrop-blur-sm border border-white/5 rounded-xl p-2 md:p-2.5">
              <div className="px-4 text-center border-r border-white/10">
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">XP Earned</div>
                <div className="text-lg md:text-xl font-bold text-white font-mono">{animatedXp}</div>
              </div>
              <div className="px-4 text-center border-r border-white/10">
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Streak Day</div>
                <div className="text-lg md:text-xl font-bold text-orange-400 font-mono">{animatedStreak}</div>
              </div>
              <div className="px-4 text-center">
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Level</div>
                <div className="text-lg md:text-xl font-bold text-purple-400 font-mono">{animatedLevel}</div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Bento Grid - Primary Stats & Roadmap */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

          {/* Level Progress (Large) - Col 4 */}
          <div className="md:col-span-4 relative overflow-hidden rounded-2xl glass-panel p-4 flex flex-col justify-between group bg-gradient-to-br from-indigo-900/20 to-transparent">
            <div className="absolute top-0 right-0 p-4 opacity-20">
              <i className="fas fa-crown text-7xl text-indigo-500 rotate-12"></i>
            </div>
            <div>
              <div className="text-indigo-400 text-xs font-mono font-bold tracking-widest mb-1.5">CURRENT LEVEL</div>
              <div className="text-4xl font-bold text-white">{animatedLevel}</div>
              <div className="text-indigo-200/60 text-[10px] mt-0.5">
                {getUserFullTitle(stats.level, selectedLanguage === 'all' ? learnedLanguages : selectedLanguage)}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-[10px] text-indigo-300/80 mb-1.5 font-mono">
                <span>PROGRESS</span>
                <span>{stats.totalXp} / {stats.requiredXp} XP</span>
              </div>
              <div className="h-2.5 bg-indigo-950 rounded-full overflow-hidden border border-indigo-500/20">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 shadow-[0_0_15px_#6366f1] transition-all duration-1000" style={{ width: `${xpProgress}%` }}></div>
              </div>
              <div className="text-right text-[10px] text-indigo-400/60 mt-1.5">Next reward: Advanced Algorithms Pattern</div>
            </div>
          </div>

          {/* Roadmap Status (Wide) - Col 8 */}
          <div className="md:col-span-8 relative overflow-hidden rounded-2xl glass-panel p-4 flex flex-col md:flex-row items-center gap-6 group hover:border-cyan-500/20 transition-colors">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <i className="fas fa-map text-cyan-400 text-sm"></i>
                </div>
                <h3 className="text-lg font-bold text-white">Career Roadmap</h3>
              </div>

              {roadmapPreview?.hasRoadmap ? (
                <>
                  <h4 className="text-base text-slate-200 mb-1">{roadmapPreview.title}</h4>
                  <p className="text-xs text-slate-500 mb-4 max-w-md line-clamp-2">{roadmapPreview.description}</p>

                  <div className="flex gap-4">
                    <div className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex flex-col items-center">
                      <span className="text-[10px] text-slate-500 uppercase">Topics</span>
                      <span className="text-base font-bold text-white font-mono">{roadmapPreview.completedTopics}/{roadmapPreview.totalTopics}</span>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 flex flex-col items-center">
                      <span className="text-[10px] text-slate-500 uppercase">Phases</span>
                      <span className="text-base font-bold text-white font-mono">{roadmapPreview.phasesCount}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-slate-400">
                  <p className="mb-3 text-sm">No active roadmap found. Start your journey today.</p>
                  <Link to="/roadmap" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-all font-bold text-xs">
                    <i className="fas fa-plus"></i> Create Roadmap
                  </Link>
                </div>
              )}
            </div>

            {roadmapPreview?.hasRoadmap && (
              <div className="relative w-32 h-32 shrink-0 flex items-center justify-center">
                {/* Circular Progress with SVG */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="50%" cy="50%" r="45%" fill="none" stroke="#1e293b" strokeWidth="6" />
                  <circle cx="50%" cy="50%" r="45%" fill="none" stroke="url(#gradient-roadmap)" strokeWidth="6"
                    strokeDasharray={`${Math.PI * 90}%`}
                    strokeDashoffset={`${Math.PI * 90 * (1 - (roadmapPreview.progress / 100))}%`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                  <defs>
                    <linearGradient id="gradient-roadmap" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#22d3ee" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-white font-mono">{Math.round(roadmapPreview.progress)}%</span>
                  <span className="text-[8px] text-slate-500 uppercase tracking-wider">COMPLETE</span>
                </div>
              </div>
            )}

            {roadmapPreview?.hasRoadmap && (
              <div className="absolute bottom-5 right-5">
                <Link to="/roadmap" className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white hover:border-cyan-400 transition-all">
                  <i className="fas fa-arrow-right text-xs"></i>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* 3. Charts & Insights Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Activity Chart (Lines) - Col Span 2 */}
          <div className="lg:col-span-2 rounded-2xl glass-panel p-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="flex items-center gap-2 text-base font-bold text-white">
                <i className="fas fa-wave-square text-cyan-400 text-sm"></i>
                Activity Log
              </h3>
              {trendSummary && (
                <div className={`flex items-center gap-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${trendSummary.growth_rate > 0 ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-slate-500/10 border-slate-500/20 text-slate-400'}`}>
                  <i className={`fas ${trendSummary.icon}`}></i>
                  {trendSummary.text}
                </div>
              )}
            </div>
            <div className="h-56 md:h-64 w-full">
              <Line data={activityChartConfig.data} options={activityChartConfig.options} />
            </div>
          </div>

          {/* Insights Panel - Col Span 1 */}
          <div className="rounded-2xl glass-panel p-4 flex flex-col">
            <h3 className="flex items-center gap-2 text-base font-bold text-white mb-4">
              <i className="fas fa-brain text-purple-400 text-sm"></i>
              AI Insights
            </h3>

            <div className="flex-1 space-y-2 overflow-y-auto pr-2 dashboard-scroll max-h-[300px]">
              {!insights ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3 opacity-60">
                  <i className="fas fa-microchip text-3xl"></i>
                  <p className="text-xs">Analyzing data patterns...</p>
                </div>
              ) : (
                <>
                  {insights.warnings?.map((w, i) => <InsightItem key={`w-${i}`} type="warning" title={w.title} description={w.description} />)}
                  {insights.strengths?.map((s, i) => <InsightItem key={`s-${i}`} type="strength" title={s.skill} description={s.message} score={s.score} />)}
                  {insights.recommendations?.map((r, i) => <InsightItem key={`r-${i}`} type="recommendation" title={r.title} description={r.description} />)}
                </>
              )}
            </div>
          </div>
        </div>

        {/* 4. Skills & Language Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Radar Chart */}
          <div className="rounded-2xl glass-panel p-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
            <h3 className="flex items-center gap-2 text-base font-bold text-white mb-4 relative z-10">
              <i className="fas fa-radar text-purple-400 text-sm"></i>
              Combat Readiness
            </h3>

            {/* Language Filter */}
            <div className="absolute top-5 right-5 z-20">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="bg-[#0f1219] border border-white/10 text-[10px] text-slate-300 rounded-lg px-2 py-1 focus:border-purple-500 outline-none"
              >
                <option value="all">ALL SYSTEMS</option>
                {learnedLanguages.map(l => (
                  <option key={l} value={l.toLowerCase()}>{l.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div className="h-56 relative z-10">
              <Radar data={skillChartConfig.data} options={skillChartConfig.options} />
            </div>
          </div>

          {/* Stats Cards Grid */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              icon="fas fa-code"
              color="cyan"
              title="Total Solved"
              value={animatedSolved}
              subtext={`Attempted: ${problemStats.attempted}`}
              delay="delay-100"
            />
            <StatCard
              icon="fas fa-bullseye"
              color="green"
              title="Success Rate"
              value={`${problemStats.successRate}%`}
              delay="delay-200"
            />
            {/* Language Distribution */}
            <div className="col-span-2 rounded-2xl glass-panel p-4">
              <h4 className="text-[10px] font-bold text-slate-500 uppercase mb-3 font-mono">Language Payload</h4>
              <div className="space-y-2.5">
                {languageProblemsList.map((item, idx) => (
                  <div key={item.lang} className="flex items-center gap-3">
                    <div className="w-16 text-[10px] font-bold text-slate-300 uppercase">{item.lang}</div>
                    <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${idx === 0 ? 'bg-cyan-400' : 'bg-purple-400'} shadow-[0_0_8px_currentColor]`} style={{ width: `${item.percent}%` }}></div>
                    </div>
                    <div className="w-8 text-right text-[10px] text-slate-500 font-mono">{item.count}</div>
                  </div>
                ))}
                {languageProblemsList.length === 0 && <div className="text-[10px] text-slate-600 text-center py-2">No data recorded</div>}
              </div>
            </div>

            {/* Badges / Mastered Skills - New Addition */}
            <div className="col-span-2 rounded-2xl glass-panel p-4 relative overflow-hidden group">
              <h4 className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase mb-3 font-mono z-10 relative">
                <i className="fas fa-medal text-yellow-500"></i>
                Collection
              </h4>
              <div className="grid grid-cols-4 gap-2 relative z-10">
                {masteredSkills.length > 0 ? (
                  masteredSkills.slice(0, 8).map((skill, idx) => (
                    <div key={idx} className="aspect-square rounded-xl bg-white/5 border border-white/10 flex flex-col items-center justify-center gap-1 hover:bg-white/10 hover:border-yellow-500/30 transition-all group/badge" title={skill.skill_name || skill}>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center text-yellow-400 group-hover/badge:scale-110 transition-transform">
                        <i className={`fas ${skill.icon || 'fa-certificate'} text-sm shadow-[0_0_10px_rgba(234,179,8,0.3)]`}></i>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-4 text-center py-4 text-[10px] text-slate-600">
                    <i className="fas fa-lock mb-2 opacity-50 block"></i>
                    No badges earned yet
                  </div>
                )}
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:bg-yellow-500/10 transition-colors"></div>
            </div>
          </div>
        </div>

        {/* 5. Timeline - "System Logs" */}
        <section className="rounded-2xl glass-panel p-5 relative overflow-hidden">
          <h3 className="flex items-center gap-2 text-lg font-bold text-white mb-5 relative pl-1">
            <i className="fas fa-history text-slate-400 text-sm"></i>
            System Logs
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {timeline.length === 0 ? (
              <div className="col-span-2 text-center text-slate-500 py-12">No activity logs found. Initiate sequence.</div>
            ) : (
              timeline.map((log, i) => (
                <div key={i} className="relative group p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-cyan-500/30 transition-all duration-300">
                  {/* Node Decorator */}
                  <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_8px_#22d3ee] opacity-50 group-hover:opacity-100 transition-opacity"></div>

                  {/* Content Container */}
                  <div className="flex flex-col gap-2">
                    {/* Header Line: Date | Topic */}
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-cyan-400 opacity-80">{log.date}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                      <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">{log.topic}</span>
                    </div>

                    {/* Title */}
                    <h4 className="text-base font-bold text-slate-200 group-hover:text-white transition-colors truncate pr-6">{log.title}</h4>

                    {/* Summary */}
                    {log.summary && (
                      <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 hover:line-clamp-none transition-all cursor-default">
                        {log.summary}
                      </p>
                    )}

                    {/* Code Toggle */}
                    {log.code && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        <details className="group/code">
                          <summary className="cursor-pointer inline-flex items-center gap-2 text-xs text-slate-500 hover:text-cyan-400 transition-colors select-none font-medium">
                            <i className="fas fa-code text-xs group-open/code:text-cyan-400"></i>
                            <span>View Code Snippet</span>
                          </summary>
                          <div className="mt-2 p-3 rounded-lg bg-[#050505] border border-white/10 overflow-x-auto max-h-48 dashboard-scroll">
                            <pre className="text-xs font-mono text-green-400">{log.code}</pre>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

      </main>
    </div>
  );
}
